import React from 'react';
import { type PortableTextComponents } from 'next-sanity';
import { Typography } from '@/app/common';
import { ResolvedLink } from './ResolvedLink';
import { hasTextContent, withInterWordSpacing } from '@/app/lib';

// Helper function to render empty blocks with consistent spacing
const renderEmptyBlock = (height: string) => <div className={height} />;

// Ensure a space between adjacent React children nodes when marks split words

type HeadingVariant = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

const renderHeading = (variant: HeadingVariant, children: React.ReactNode, value: unknown) => {
  if (!hasTextContent(value)) {
    const heightMap: Record<HeadingVariant, string> = {
      h1: 'h-8',
      h2: 'h-7',
      h3: 'h-6',
      h4: 'h-6',
      h5: 'h-5',
      h6: 'h-5',
    };
    return renderEmptyBlock(heightMap[variant]);
  }

  return <Typography variant={variant}>{withInterWordSpacing(children)}</Typography>;
};

export const portableTextComponents: PortableTextComponents = {
  block: {
    // Headings - use Sora font (inherited from Typography component)
    h1: ({ children, value }) => renderHeading('h1', children, value),
    h2: ({ children, value }) => renderHeading('h2', children, value),
    h3: ({ children, value }) => renderHeading('h3', children, value),
    h4: ({ children, value }) => renderHeading('h4', children, value),
    h5: ({ children, value }) => renderHeading('h5', children, value),
    h6: ({ children, value }) => renderHeading('h6', children, value),

    // Paragraphs - use Open Sans (inherited from Typography component)
    normal: ({ children, value }) => {
      if (!hasTextContent(value)) {
        return renderEmptyBlock('h-6');
      }

      return <Typography variant="bodyMd">{withInterWordSpacing(children)}</Typography>;
    },

    // Blockquotes
    blockquote: ({ children, value }) => {
      if (!hasTextContent(value)) {
        return renderEmptyBlock('h-6');
      }

      return (
        <blockquote className="border-l-4 border-secondary-300 pl-4 italic font-openSans text-secondary-400">
          {children}
        </blockquote>
      );
    },

    // Code blocks
    code: ({ children, value }) => {
      if (!hasTextContent(value)) {
        return renderEmptyBlock('h-6');
      }

      return (
        <pre className="bg-secondary-100 p-4 rounded-md overflow-x-auto font-mono text-sm text-secondary-400">
          <code>{children}</code>
        </pre>
      );
    },
  },

  // List components - use Open Sans for body text
  list: {
    bullet: ({ children }) => (
      <ul className="list-disc list-outside space-y-2 pl-6 font-openSans text-secondary-400">{children}</ul>
    ),
    number: ({ children }) => (
      <ol className="list-decimal list-outside space-y-2 pl-6 font-openSans text-secondary-400">{children}</ol>
    ),
  },

  // List item components
  listItem: {
    bullet: ({ children }) => <li className="font-openSans text-secondary-400">{withInterWordSpacing(children)}</li>,
    number: ({ children }) => <li className="font-openSans text-secondary-400">{withInterWordSpacing(children)}</li>,
  },

  // Inline marks (text formatting)
  marks: {
    strong: ({ children }) => (
      <strong className="font-bold font-openSans text-secondary-400">{withInterWordSpacing(children)}</strong>
    ),
    em: ({ children }) => <em className="italic font-openSans text-secondary-400">{withInterWordSpacing(children)}</em>,
    code: ({ children }) => (
      <code className="bg-secondary-100 px-1 py-0.5 rounded text-sm font-mono text-secondary-400">
        {withInterWordSpacing(children)}
      </code>
    ),
    underline: ({ children }) => (
      <span className="underline font-openSans text-secondary-400">{withInterWordSpacing(children)}</span>
    ),
    strikethrough: ({ children }) => (
      <span className="line-through font-openSans text-secondary-400">{withInterWordSpacing(children)}</span>
    ),
    link: ({ children, value: link }) => {
      // Handle different link structures
      const linkData = link || {};

      // If it's a markDef link, extract the href
      if (linkData.href) {
        // Check if it's a URL (external link)
        const isUrl =
          linkData.href.startsWith('http://') ||
          linkData.href.startsWith('https://') ||
          linkData.href.startsWith('//') ||
          linkData.href.startsWith('mailto:') ||
          linkData.href.startsWith('tel:');

        // URLs always open in new tab, internal links open in new tab if isOpenInNewTab is true
        const shouldOpenInNewTab = isUrl || linkData.isOpenInNewTab || linkData.openInNewTab;

        return (
          <a
            href={linkData.href}
            target={shouldOpenInNewTab ? '_blank' : undefined}
            rel={shouldOpenInNewTab ? 'noopener noreferrer' : undefined}
            className="underline text-primary font-semibold hover:text-primary-500 transition-colors font-openSans"
          >
            {withInterWordSpacing(children)}
          </a>
        );
      }

      // Use ResolvedLink for complex link structures
      return (
        <ResolvedLink
          link={linkData}
          className="underline text-primary font-semibold hover:text-primary-500 transition-colors font-openSans"
        >
          {withInterWordSpacing(children)}
        </ResolvedLink>
      );
    },
  },

  // Hard breaks
  hardBreak: () => <br />,

  // Unknown types fallback
  unknownType: ({ value }) => (
    <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
      <Typography variant="bodyMd">
        Unknown block type: <code>{value._type}</code>
      </Typography>
    </div>
  ),
};
